import Add from '@mui/icons-material/Add'
import Campaign from '@mui/icons-material/CampaignOutlined'
import { Alert, Button, Chip, List, ListItem, ListItemText, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'preact/hooks'
import { api } from '../api/client'
import type { Announcement } from '../api/generated/Announcement'
import type { MemberRole } from '../api/generated/MemberRole'
import type { Shift } from '../api/generated/Shift'
import { useApi } from '../api/useApi'
import { formatSentAt } from '../conversations/format'
import { useSignalReload } from '../live/useLiveReload'
import { planningDays } from '../planning/grid'
import { AnnouncementDialog } from './AnnouncementDialog'
import { describeAudience, unreadIds } from './format'

/**
 * Announcements, most recent first: all of the event's for an organizer, who writes them; those
 * received for a volunteer, marked read once shown.
 */
export function AnnouncementList({
  slug,
  timezone,
  role,
}: {
  slug: string
  timezone: string
  role: MemberRole
}) {
  const organizer = role === 'organizer'
  const announcements = useApi<Announcement[]>(`/events/${slug}/announcements`)
  const shifts = useApi<Shift[]>(organizer ? `/events/${slug}/shifts` : null)
  useSignalReload(`/events/${slug}/conversations/live`, announcements.reload)
  const [writing, setWriting] = useState(false)
  const [highlighted, setHighlighted] = useState<ReadonlySet<string>>(new Set())
  const unread = announcements.data?.some((a) => a.unread) ?? false

  useEffect(() => {
    if (!announcements.data) return
    setHighlighted((seen) => unreadIds(announcements.data ?? [], seen))
    if (unread) api.post(`/events/${slug}/announcements/read`, {}).catch(() => {})
  }, [announcements.data])

  return (
    <Stack spacing={1}>
      {organizer ? (
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setWriting(true)}
          sx={{ alignSelf: 'flex-start' }}
        >
          Nouvelle annonce
        </Button>
      ) : (
        <Typography variant="h6">Annonces</Typography>
      )}
      {announcements.error && <Alert severity="error">{announcements.error}</Alert>}
      {announcements.data?.length === 0 &&
        (organizer ? (
          <Typography color="text.secondary">
            Prévenez d'un coup tous les bénévoles validés, ou ceux d'un jour : météo, horaires,
            consignes.
          </Typography>
        ) : (
          <>
            <Typography color="text.secondary">Aucune annonce pour l'instant.</Typography>
            <Button href={`/events/${slug}/planning`} sx={{ alignSelf: 'flex-start' }}>
              Voir mon planning
            </Button>
          </>
        ))}
      <List dense disablePadding>
        {announcements.data?.map((announcement) => (
          <ListItem key={announcement.id} disableGutters sx={{ alignItems: 'flex-start', gap: 1 }}>
            <Campaign color="action" sx={{ mt: 0.75 }} />
            <ListItemText
              primary={announcement.body}
              secondary={`${formatSentAt(announcement.sent_at, timezone)} · ${describeAudience(announcement)}`}
              slotProps={{
                primary: {
                  sx: {
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    fontWeight: highlighted.has(announcement.id) ? 'bold' : undefined,
                  },
                },
              }}
            />
            {highlighted.has(announcement.id) && (
              <Chip label="Nouveau" color="error" size="small" sx={{ mt: 0.5 }} />
            )}
          </ListItem>
        ))}
      </List>
      {writing && (
        <AnnouncementDialog
          slug={slug}
          days={planningDays(shifts.data ?? [])}
          onSent={() => {
            setWriting(false)
            announcements.reload()
          }}
          onClose={() => setWriting(false)}
        />
      )}
    </Stack>
  )
}
